import React, { useState } from "react";
import { useHSE } from "../lib/store";
import { Button } from "../components/ui/button";
import { FileText, Download, Printer, FileSpreadsheet } from "lucide-react";
import { exportCSV, exportXLSX, exportElementPDF } from "../lib/exportUtils";
import { toast } from "sonner";

const REPORTS = [
  { id: "monthly", title: "TEST Site HSE Report", desc: "Complete monthly HSE performance summary with KPIs, incidents, actions", store: "monthlyKPI" },
  { id: "weekly", title: "Weekly HSE Report", desc: "Rolling 7-day snapshot of active permits, observations & actions", store: "observations" },
  { id: "incident-investigation", title: "Incident Investigation Report", desc: "Detailed RCA-driven investigation report", store: "incidents" },
  { id: "audit", title: "Audit Report", desc: "Internal, external, client and ISO 45001 audits", store: "audits" },
  { id: "ncr-sor", title: "NCR / SOR Report", desc: "Non-conformance & site observation reports", store: "ncrs" },
  { id: "training", title: "Training Compliance Report", desc: "Employee training matrix with expiry status", store: "trainings" },
  { id: "ptw", title: "PTW Status Report", desc: "Permit-to-work register with linked RA/MS", store: "ptws" },
  { id: "contractor", title: "Contractor Performance Report", desc: "Contractor HSE rating with manhours, incidents, audits", store: "contractorPerformance" },
  { id: "legal", title: "Legal Compliance Report", desc: "OSHA · ISO 45001 · ISO 14001 · Saudi regulations", store: "legalCompliance" },
  { id: "action", title: "Action Tracking Report", desc: "Open, in-progress and overdue actions", store: "actions" },
  { id: "equipment", title: "Equipment Expiry Report", desc: "TPI certificate expiry tracker", store: "equipment" },
  { id: "drills", title: "Emergency Drill Report", desc: "Fire, evacuation, rescue drills — lessons learned", store: "drills" },
];

export default function Reports() {
  const { data, filters, setFilters, siteNames } = useHSE();
  const [generated, setGenerated] = useState(null);

  const generate = (rep) => {
    setGenerated(rep);
    toast.success(`${rep.title} generated`);
  };

  const exportPdf = async () => {
    if (!generated) return;
    await exportElementPDF("report-content", `${generated.id}-report.pdf`);
    toast.success("PDF exported");
  };

  const exportCsv = () => {
    if (!generated) return;
    const rows = data[generated.store] || [];
    if (Array.isArray(rows)) exportCSV(rows, `${generated.id}.csv`);
    toast.success("CSV exported");
  };

  const exportXls = () => {
    if (!generated) return;
    const rows = data[generated.store] || [];
    if (Array.isArray(rows)) exportXLSX(rows, `${generated.id}.xlsx`, generated.title);
    toast.success("Excel exported");
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-[#0E2336]">Operational Reports</h1>
        <div className="text-sm text-slate-500">Live operational views for day-to-day analysis — PDF · Excel · CSV. Need a reviewed, locked record? <a className="text-blue-700 font-semibold" href="/reports-center">Open Governed Reports &amp; Approvals</a>.</div>
      </div>

      <div className="command-card p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Month</label>
            <select value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
              <option value="All">All</option>
              {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => <option key={i} value={String(i)}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Year</label>
            <select value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
              {["2024", "2025", "2026", "2027"].map((y) => <option key={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Site</label>
            <select value={filters.site} onChange={(e) => setFilters({ ...filters, site: e.target.value })}
              className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
              <option>All Sites</option>
              {siteNames.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Department</label>
            <select value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })}
              className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
              <option>All Departments</option>
              {data.settings.departments.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {REPORTS.map((r) => {
          const count = Array.isArray(data[r.store]) ? data[r.store].length : "—";
          return (
            <div key={r.id} className="command-card p-4 hover:shadow-md transition">
              <div className="flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                  style={{ background: "#DCFCE7", color: "#18B84F" }}>
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-[13px] text-[#082B4D] leading-tight">{r.title}</div>
                  <div className="text-[11px] text-slate-500 line-clamp-2 mt-1">{r.desc}</div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-[10px] text-slate-500 font-mono">
                <span>Records: <b className="text-[#082B4D]">{count}</b></span>
                <span>{filters.site} · {filters.year}</span>
              </div>
              <div className="mt-3 flex gap-1.5">
                <Button data-testid={`report-${r.id}-generate`} onClick={() => generate(r)} size="sm"
                  className="flex-1 h-8 text-xs gap-1.5 font-semibold text-white" style={{ background: "#082B4D" }}>
                  Generate
                </Button>
                <Button
                  data-testid={`report-${r.id}-pdf`}
                  onClick={() => { setGenerated(r); setTimeout(exportPdf, 200); }}
                  size="sm" variant="outline" className="h-8 text-xs px-2" title="Quick PDF"
                >
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button
                  data-testid={`report-${r.id}-xlsx`}
                  onClick={() => {
                    const rows = data[r.store] || [];
                    if (Array.isArray(rows) && rows.length) exportXLSX(rows, `${r.id}.xlsx`, r.title);
                    toast.success(`${r.title} — Excel exported`);
                  }}
                  size="sm" variant="outline" className="h-8 text-xs px-2" title="Quick Excel"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {generated && (
        <div className="command-card p-5">
          <div className="flex items-center justify-between mb-4 no-print">
            <div>
              <h2 className="font-heading font-bold text-lg text-[#0E2336]">{generated.title}</h2>
              <div className="text-xs text-slate-500">Generated {new Date().toLocaleString()}</div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="report-print-btn" variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => window.print()}>
                <Printer className="w-3.5 h-3.5" /> Print
              </Button>
              <Button data-testid="report-csv-btn" variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={exportCsv}>
                <Download className="w-3.5 h-3.5" /> CSV
              </Button>
              <Button data-testid="report-xlsx-btn" variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={exportXls}>
                <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
              </Button>
              <Button data-testid="report-pdf-btn" size="sm" className="h-8 text-xs gap-1.5 font-semibold text-white" style={{ background: "#18B84F" }} onClick={exportPdf}>
                <Download className="w-3.5 h-3.5" /> PDF
              </Button>
            </div>
          </div>

          <div id="report-content" className="bg-white rounded-lg p-4">
            <div className="border-b border-slate-200 pb-4 mb-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#18B84F] font-semibold">HSE Command Center · ISO 45001 Corporate Report</div>
              <h2 className="font-heading text-2xl font-bold text-[#0E2336] mt-1">{generated.title}</h2>
              <div className="text-sm text-slate-500 mt-1">
                Client: <b>{data.settings.client || "Client Name"}</b> · Company: <b>{data.settings.company || "Company Name"}</b> · Year {filters.year} · {filters.site}
              </div>
            </div>
            <ReportBody report={generated} data={data} />
            <div className="mt-6 pt-4 border-t border-slate-200 text-[10px] text-slate-400 font-mono">
              {data.settings.reportFooter || "Generated by HSE Command Center — Confidential"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportBody({ report, data }) {
  const rows = Array.isArray(data[report.store]) ? data[report.store] : [];
  if (!Array.isArray(rows) || rows.length === 0) {
    return <div className="text-slate-400 italic py-8 text-center">No data available for this report.</div>;
  }
  const cols = Object.keys(rows[0]).slice(0, 9);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead style={{ background: "#082B4D" }}>
          <tr>{cols.map((c) => <th key={c} className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-white font-semibold">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.slice(0, 50).map((r, i) => (
            <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
              {cols.map((c) => <td key={c} className="py-1.5 px-2 text-slate-700 border-b border-slate-100">{String(r[c] ?? "")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 50 && <div className="text-[11px] text-slate-400 mt-2">Showing 50 of {rows.length} records</div>}
    </div>
  );
}
